/**
 * Column models for the four Call Reports.
 *
 * All four live in one file because they share a vocabulary — a duration is a
 * duration, "Campaign" is 190px wide everywhere — and splitting them into four
 * files is how the same column ends up 190px in one report and 200px in the
 * next.
 *
 * Each catalog pins its identity column (`stickyLeft: 0, locked: true`). With
 * thirty-odd columns scrolling horizontally, a row whose call id has scrolled
 * off the left edge is unreadable.
 *
 * The band columns (Answered 1-5, 6-10, …) are GENERATED from one ladder
 * rather than typed out, mirroring `WAIT_BANDS` on the API side: eight
 * hand-written entries per group is eight chances for a label to disagree with
 * the boundary the query actually used.
 */
import type { ReportColumnDef } from '../agent-performance/columns.js'

export const CALL_HISTORY_REPORT_ID = 'call.history'
export const IVR_SELECTION_REPORT_ID = 'call.ivr-selection'
export const INBOUND_CALL_REPORT_ID = 'call.inbound'
export const OUTBOUND_CALL_REPORT_ID = 'call.outbound'
export const AGENT_PRODUCTIVITY_PER_INTERVAL_REPORT_ID = 'call.agent-productivity-per-interval'

/** Must stay in step with `WAIT_BANDS` in the API's calls/shared.ts. */
const BANDS = ['1-5', '6-10', '11-20', '21-30', '31-60', '61-90', '91-120', '>120'] as const
const BAND_KEYS = ['Lt5', 'Lt10', 'Lt20', 'Lt30', 'Lt60', 'Lt90', 'Lt120', 'Gt120'] as const

function bandColumns(prefix: string, label: string): ReportColumnDef[] {
  return BAND_KEYS.map((key, i) => ({
    key: `${prefix}${key}`,
    label: `${label} (${BANDS[i]})`,
    width: 200,
    align: 'right' as const,
    type: 'number' as const,
  }))
}

// ─────────────────────────────── Call History ───────────────────────────────

// Order follows the legacy agent-portal Call Recordings report operators
// know: recording controls first, then parties, routing, timings, outcome.
export const CALL_HISTORY_COLUMNS: ReportColumnDef[] = [
  { key: 'uniqueId', label: 'Call ID', width: 240, stickyLeft: 0, locked: true, type: 'mono' },
  // 'recording' rather than 'boolean': the cell renders an inline player, which
  // needs `recordingId` off the same row — hence the row-level renderer.
  { key: 'hasRecording', label: 'Recording', width: 150, align: 'center', type: 'recording' },
  { key: 'transcriptStatus', label: 'Transcript', width: 150, align: 'center', type: 'transcript' },
  { key: 'fromNumber', label: 'Source', width: 170, type: 'text' },
  { key: 'toNumber', label: 'Destination', width: 170, type: 'text' },
  { key: 'direction', label: 'Call Type', width: 130, align: 'center', type: 'badge' },
  { key: 'campaignName', label: 'Campaign', width: 190, type: 'text' },
  { key: 'queueName', label: 'Queue', width: 180, type: 'text' },
  { key: 'destinationType', label: 'Destination Type', width: 180, type: 'text' },
  { key: 'agentName', label: 'Agent', width: 200, type: 'text' },
  { key: 'agentExtension', label: 'Extension', width: 130, type: 'text' },
  { key: 'startTime', label: 'Start Time', width: 200, type: 'datetime' },
  { key: 'answerTime', label: 'Answer Time', width: 200, type: 'datetime' },
  { key: 'endTime', label: 'End Time', width: 200, type: 'datetime' },
  { key: 'durationSec', label: 'Call Duration', width: 170, align: 'right', type: 'duration' },
  { key: 'callStatus', label: 'Call Status', width: 150, align: 'center', type: 'badge' },
  { key: 'hangupCause', label: 'Hangup Cause', width: 200, type: 'text' },
  { key: 'hangupParty', label: 'Hangup First', width: 150, align: 'center', type: 'badge' },
  { key: 'ringSec', label: 'Ringing Time', width: 160, align: 'right', type: 'duration' },
  { key: 'holdSec', label: 'Hold Time', width: 160, align: 'right', type: 'duration' },
  { key: 'talkSec', label: 'Talk Time', width: 160, align: 'right', type: 'duration' },
  { key: 'queueEnteredAt', label: 'Queue Start Time', width: 200, type: 'datetime' },
  { key: 'queueAnsweredAt', label: 'Queue Answer Time', width: 200, type: 'datetime' },
  { key: 'queueExitedAt', label: 'Queue End Time', width: 200, type: 'datetime' },
  { key: 'queueAnswerStatus', label: 'Queue Answer Status', width: 200, align: 'center', type: 'badge' },
  { key: 'dispositionName', label: 'Disposition', width: 190, type: 'text' },
  { key: 'notes', label: 'Notes', width: 260, type: 'text' },
  { key: 'bucket', label: 'Interval', width: 200, type: 'datetime' },
  { key: 'did', label: 'DID', width: 170, type: 'text' },
  { key: 'didName', label: 'DID Name', width: 190, type: 'text' },
  { key: 'wrapUpSec', label: 'Wrap-up Time', width: 170, align: 'right', type: 'duration' },
  { key: 'queueWaitSec', label: 'Queue Wait', width: 160, align: 'right', type: 'duration' },
  { key: 'handleSec', label: 'Handle Time', width: 170, align: 'right', type: 'duration' },
  { key: 'ivrName', label: 'IVR', width: 180, type: 'text' },
  { key: 'ivrDigits', label: 'IVR Option', width: 150, type: 'text' },
  { key: 'ivrSelection', label: 'IVR Selection', width: 220, type: 'text' },
  { key: 'ivrSec', label: 'IVR Time', width: 150, align: 'right', type: 'duration' },
  { key: 'trunkName', label: 'Trunk', width: 170, type: 'text' },
  { key: 'recordingSec', label: 'Recording Length', width: 190, align: 'right', type: 'duration' },
  { key: 'abandoned', label: 'Abandoned', width: 140, align: 'center', type: 'boolean' },
  { key: 'mos', label: 'MOS', width: 110, align: 'right', type: 'number' },
  { key: 'jitterMs', label: 'Jitter (ms)', width: 130, align: 'right', type: 'number' },
  { key: 'packetsLost', label: 'Packets Lost', width: 150, align: 'right', type: 'number' },
  { key: 'cost', label: 'Cost', width: 130, align: 'right', type: 'number' },
]

// ─────────────────────────────── IVR Selection ──────────────────────────────

export const IVR_SELECTION_COLUMNS: ReportColumnDef[] = [
  { key: 'uniqueId', label: 'Call ID', width: 240, stickyLeft: 0, locked: true, type: 'mono' },
  { key: 'bucket', label: 'Interval', width: 200, type: 'datetime' },
  { key: 'ivrName', label: 'IVR Name', width: 200, type: 'text' },
  { key: 'digit', label: 'IVR Option', width: 130, align: 'center', type: 'badge' },
  { key: 'optionLabel', label: 'IVR Label', width: 220, type: 'text' },
  { key: 'pressedAt', label: 'Pressed At', width: 200, type: 'datetime' },
  { key: 'timeToPressSec', label: 'Time to Press', width: 170, align: 'right', type: 'duration' },
  { key: 'direction', label: 'Call Type', width: 130, align: 'center', type: 'badge' },
  { key: 'callStartTime', label: 'Call Start Date', width: 200, type: 'datetime' },
  { key: 'callEndTime', label: 'Call End Date', width: 200, type: 'datetime' },
  { key: 'totalSec', label: 'Total Time Of The Call', width: 210, align: 'right', type: 'duration' },
  { key: 'talkSec', label: 'Talk Time Of The Call', width: 210, align: 'right', type: 'duration' },
  { key: 'ivrEnteredAt', label: 'IVR Entered', width: 200, type: 'datetime' },
  { key: 'ivrExitedAt', label: 'IVR Exited', width: 200, type: 'datetime' },
  { key: 'ivrSec', label: 'IVR Time', width: 150, align: 'right', type: 'duration' },
  { key: 'exitReason', label: 'IVR Exit Reason', width: 180, align: 'center', type: 'badge' },
  { key: 'ivrDestinationType', label: 'Routed To', width: 170, type: 'text' },
  { key: 'campaignName', label: 'Campaign', width: 190, type: 'text' },
  { key: 'queueName', label: 'Queue', width: 180, type: 'text' },
  { key: 'agentName', label: 'User', width: 200, type: 'text' },
  { key: 'agentExtension', label: 'Extension', width: 130, type: 'text' },
  { key: 'customerPhone', label: 'Customer Phone', width: 180, type: 'text' },
  { key: 'did', label: 'DID', width: 170, type: 'text' },
  { key: 'callStatus', label: 'Call Status', width: 150, align: 'center', type: 'badge' },
]

// ────────────────────────────── Inbound (interval) ──────────────────────────

export const INBOUND_CALL_COLUMNS: ReportColumnDef[] = [
  { key: 'bucketStart', label: 'Start Interval', width: 200, stickyLeft: 0, locked: true, type: 'datetime' },
  { key: 'bucketEnd', label: 'End Interval', width: 200, type: 'datetime' },
  { key: 'campaignName', label: 'Campaign', width: 190, type: 'text' },
  { key: 'queueName', label: 'Queue', width: 180, type: 'text' },
  { key: 'totalCalls', label: 'Total Calls', width: 150, align: 'right', type: 'number' },
  { key: 'answered', label: 'Total Answered Calls', width: 200, align: 'right', type: 'number' },
  { key: 'answeredPct', label: 'Answered (%)', width: 170, align: 'right', type: 'percent' },
  { key: 'abandoned', label: 'Total Abandoned Calls', width: 210, align: 'right', type: 'number' },
  { key: 'abandonedPct', label: 'Total Abandoned Calls (%)', width: 230, align: 'right', type: 'percent' },
  { key: 'ivrAbandoned', label: 'IVR Abandoned Calls', width: 200, align: 'right', type: 'number' },
  { key: 'queueAbandoned', label: 'Queue Abandoned Calls', width: 220, align: 'right', type: 'number' },
  { key: 'overflowed', label: 'Overflowed', width: 160, align: 'right', type: 'number' },
  { key: 'timedOut', label: 'Timed Out', width: 150, align: 'right', type: 'number' },
  { key: 'enteredQueue', label: 'Entered Queue', width: 170, align: 'right', type: 'number' },
  { key: 'totalTalkSec', label: 'Total Talk Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgTalkSec', label: 'Average Talk Time', width: 200, align: 'right', type: 'duration' },
  { key: 'totalHandleSec', label: 'Total Handle Time', width: 200, align: 'right', type: 'duration' },
  { key: 'avgHandleSec', label: 'Average Handle Time', width: 210, align: 'right', type: 'duration' },
  { key: 'totalQueueSec', label: 'Total Queue Time', width: 190, align: 'right', type: 'duration' },
  { key: 'avgQueueSec', label: 'Average Queue Time', width: 200, align: 'right', type: 'duration' },
  { key: 'maxQueueSec', label: 'Max Queue Time', width: 190, align: 'right', type: 'duration' },
  { key: 'totalIvrSec', label: 'Total IVR Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgIvrSec', label: 'Avg IVR Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgRingSec', label: 'Average Ring Time', width: 190, align: 'right', type: 'duration' },
  ...bandColumns('answered', 'Answered'),
  ...bandColumns('queueAbandoned', 'Queue Abandoned'),
  ...bandColumns('ivrAbandoned', 'IVR Abandoned'),
]

// ───────────────────────────── Outbound (interval) ──────────────────────────

export const OUTBOUND_CALL_COLUMNS: ReportColumnDef[] = [
  { key: 'bucketStart', label: 'Start Interval', width: 200, stickyLeft: 0, locked: true, type: 'datetime' },
  { key: 'bucketEnd', label: 'End Interval', width: 200, type: 'datetime' },
  { key: 'campaignName', label: 'Campaign', width: 190, type: 'text' },
  { key: 'agentName', label: 'User', width: 200, type: 'text' },
  { key: 'agentExtension', label: 'Extension', width: 130, type: 'text' },
  { key: 'callsOffered', label: 'Calls Offered', width: 170, align: 'right', type: 'number' },
  { key: 'callsAnswered', label: 'Calls Answered', width: 180, align: 'right', type: 'number' },
  { key: 'connectPct', label: 'Connect Rate (%)', width: 190, align: 'right', type: 'percent' },
  { key: 'callsUnanswered', label: 'Calls Unanswered', width: 190, align: 'right', type: 'number' },
  { key: 'busy', label: 'Busy', width: 130, align: 'right', type: 'number' },
  { key: 'noAnswer', label: 'No Answer', width: 150, align: 'right', type: 'number' },
  { key: 'rejected', label: 'Rejected / Failed', width: 190, align: 'right', type: 'number' },
  { key: 'abandoned', label: 'Calls Abandoned', width: 190, align: 'right', type: 'number' },
  { key: 'abandonedPct', label: 'Calls Abandoned (%)', width: 210, align: 'right', type: 'percent' },
  { key: 'uniqueDestinations', label: 'Unique Destinations', width: 200, align: 'right', type: 'number' },
  { key: 'totalTalkSec', label: 'Total Talk Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgTalkSec', label: 'Average Talk Time', width: 200, align: 'right', type: 'duration' },
  { key: 'totalHandleSec', label: 'Total Handle Time', width: 200, align: 'right', type: 'duration' },
  { key: 'avgHandleSec', label: 'Average Handle Time (AHT)', width: 240, align: 'right', type: 'duration' },
  { key: 'totalRingSec', label: 'Total Ring Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgRingSec', label: 'Average Ring Time', width: 190, align: 'right', type: 'duration' },
  { key: 'totalCost', label: 'Total Cost', width: 150, align: 'right', type: 'number' },
  ...bandColumns('answered', 'Answered'),
]

// ─────────────────────────── Agent Productivity Per Interval ──────────────────

export const AGENT_PRODUCTIVITY_PER_INTERVAL_COLUMNS: ReportColumnDef[] = [
  { key: 'bucketStart', label: 'Start Interval', width: 200, stickyLeft: 0, locked: true, type: 'datetime' },
  { key: 'bucketEnd', label: 'End Interval', width: 200, type: 'datetime' },
  { key: 'campaignName', label: 'Campaign', width: 190, type: 'text' },
  { key: 'agentName', label: 'Agent', width: 200, type: 'text' },
  { key: 'agentId', label: 'Agent ID', width: 130, type: 'text' },
  { key: 'totalCalls', label: 'Total Calls', width: 150, align: 'right', type: 'number' },
  { key: 'totalInbound', label: 'Total Inbound', width: 150, align: 'right', type: 'number' },
  { key: 'totalOutbound', label: 'Total Outbound', width: 160, align: 'right', type: 'number' },
  { key: 'totalRingtimeInbound', label: 'Ringtime Inbound', width: 170, align: 'right', type: 'duration' },
  { key: 'totalRingtimeOutbound', label: 'Ringtime Outbound', width: 180, align: 'right', type: 'duration' },
  { key: 'answeredTotal', label: 'Answered Total', width: 160, align: 'right', type: 'number' },
  { key: 'totalAbandoned', label: 'Total Abandoned', width: 170, align: 'right', type: 'number' },
  { key: 'totalInboundTalkTime', label: 'Inbound Talk Time', width: 190, align: 'right', type: 'duration' },
  { key: 'totalOutboundTalkTime', label: 'Outbound Talk Time', width: 200, align: 'right', type: 'duration' },
  { key: 'totalInboundHandleTime', label: 'Inbound Handle Time', width: 200, align: 'right', type: 'duration' },
  { key: 'totalOutboundHandleTime', label: 'Outbound Handle Time', width: 210, align: 'right', type: 'duration' },
  { key: 'totalInboundHoldTime', label: 'Inbound Hold Time', width: 190, align: 'right', type: 'duration' },
  { key: 'totalOutboundHoldTime', label: 'Outbound Hold Time', width: 200, align: 'right', type: 'duration' },
  { key: 'totalWaitTime', label: 'Total Wait Time', width: 180, align: 'right', type: 'duration' },
  { key: 'avgWaitTime', label: 'Average Wait Time', width: 200, align: 'right', type: 'duration' },
  { key: 'loginTime', label: 'Login Time', width: 170, align: 'right', type: 'duration' },
  { key: 'readyTime', label: 'Ready Time', width: 170, align: 'right', type: 'duration' },
  { key: 'availableTime', label: 'Available Time', width: 180, align: 'right', type: 'duration' },
  { key: 'breakTime', label: 'Break Time', width: 170, align: 'right', type: 'duration' },
]

/**
 * Build the `{ defaults, sanitize }` pair a report page hands to
 * `useReportView`.
 *
 * `sanitize` drops unknown and duplicated keys from the saved arrangement and
 * forces the locked column back in — a saved view from before a column was
 * renamed must degrade to a usable table, not to an empty one.
 */
export function columnView(catalog: ReportColumnDef[]): {
  defaults: string[]
  sanitize: (saved: unknown) => string[]
} {
  const defaults = catalog.map((c) => c.key)
  const known = new Set(defaults)

  return {
    defaults,
    sanitize: (saved: unknown): string[] => {
      if (!Array.isArray(saved)) return defaults
      const seen = new Set<string>()
      const ordered: string[] = []
      for (const key of saved) {
        if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
        seen.add(key)
        ordered.push(key)
      }
      for (const col of catalog) {
        if (col.locked && !seen.has(col.key)) {
          seen.add(col.key)
          ordered.unshift(col.key)
        }
      }
      return ordered.length > 0 ? ordered : defaults
    },
  }
}

/** Read a cell for the search filter — rows are untyped API objects. */
export function readCallCell(row: Record<string, unknown>, key: string): unknown {
  return row[key]
}
